/**
 * useCreateStoryInvitees — React Query mutation that batch-invites members
 * to a story. Mirrors useCreateEmbedToken's shape (single onError callback,
 * the caller owns loading/error UI state).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createStoryInvitees } from "../services/storyInvitees/storyInviteesApi";
import { queryKeys } from "../services/queryKeys";
import type { QueryKey } from "@tanstack/react-query";
import type { CreateInviteesResponse, InviteeRequest } from "../services/storyInvitees/storyInvitees.types";

/**
 * Matches `queryKeys.stories.members(workspaceId, storyId)` — the card's
 * avatar-stack cache — for the given story, any workspace. A predicate
 * rather than the exact key because this hook (and its ShareStoryModal
 * caller) is never handed a workspaceId, only a storyId.
 */
const isStoryMembersKey = (storyId: string) => (queryKey: QueryKey) =>
  queryKey[0] === "stories" && queryKey[2] === storyId && queryKey[3] === "members";

interface UseCreateStoryInviteesOptions {
  onError?: (err: Error) => void;
}

export const useCreateStoryInvitees = (
  options: UseCreateStoryInviteesOptions = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateInviteesResponse,
    Error,
    { storyId: string; invitees: InviteeRequest[] }
  >({
    mutationFn: ({ storyId, invitees }) =>
      createStoryInvitees(storyId, { invitees }),
    onSuccess: (_data, { storyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storyInvitees.list(storyId),
      });
      // `storyInvitees.list` above is a separate cache from the members list
      // the Data Stories/Home card's avatar stack reads (`stories.members`,
      // `staleTime: Infinity`) — without this, a story invite never shows up
      // on the card until a hard refresh or the cache falls out after 5 min
      // unobserved.
      queryClient.invalidateQueries({
        predicate: (query) => isStoryMembersKey(storyId)(query.queryKey),
      });
    },
    onError: (err) => options.onError?.(err),
  });
};
